import { TAX_CODES } from '../types/invoice'

const GST_RATE = 0.1 // 10% GST for I and E codes

export interface TaxCalculationResult {
  subtotal: number
  taxAmt: number
  total: number
}

/**
 * Get tax rate for a given tax code
 */
export function getTaxRate(taxCode: string): number {
  if (taxCode === 'I' || taxCode === 'E') return GST_RATE
  return 0
}

/**
 * Recalculate subtotal and tax based on new tax code
 *
 * Tax Code Behavior:
 * - I (Inclusive): Total includes tax. When changing FROM I, keep total, recalculate subtotal and tax
 * - E (Exclusive): Tax added on top. When changing FROM E, keep subtotal, recalculate tax and total
 * - Z/R/N (Zero-rated): No tax. Subtotal = Total, Tax = 0
 *
 * When changing between I and E:
 * - I → E: Keep subtotal (it's already ex-tax), recalculate tax and total
 * - E → I: Keep total (make it tax-inclusive), recalculate subtotal and tax
 */
export function recalculateAmountsOnTaxCodeChange(
  currentSubtotal: number,
  currentTaxAmt: number,
  currentTotal: number,
  newTaxCode: string,
  oldTaxCode?: string,
): TaxCalculationResult {
  const newRate = getTaxRate(newTaxCode)
  const oldRate = oldTaxCode ? getTaxRate(oldTaxCode) : 0

  // For zero-rated codes (Z, R, N)
  if (newRate === 0) {
    return {
      subtotal: currentTotal,
      taxAmt: 0,
      total: currentTotal,
    }
  }

  // Changing TO taxable code (I or E) FROM zero-rated
  if (oldRate === 0 && newRate > 0) {
    // When coming from zero-rated, treat total as tax-inclusive
    const subtotal = currentTotal / (1 + newRate)
    const taxAmt = currentTotal - subtotal

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      taxAmt: Math.round(taxAmt * 100) / 100,
      total: currentTotal,
    }
  }

  // I → E: Subtotal stays same (it's already ex-tax), recalculate tax and total
  if (oldTaxCode === 'I' && newTaxCode === 'E') {
    // The current total is tax-inclusive, so back-calculate the subtotal
    const subtotal = currentTotal / (1 + GST_RATE)
    const taxAmt = subtotal * GST_RATE
    const total = subtotal + taxAmt

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      taxAmt: Math.round(taxAmt * 100) / 100,
      total: Math.round(total * 100) / 100,
    }
  }

  // E → I: Total stays same (make it tax-inclusive), recalculate subtotal and tax
  if (oldTaxCode === 'E' && newTaxCode === 'I') {
    // Keep the current total, back-calculate subtotal
    const total = currentTotal
    const subtotal = total / (1 + GST_RATE)
    const taxAmt = total - subtotal

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      taxAmt: Math.round(taxAmt * 100) / 100,
      total: Math.round(total * 100) / 100,
    }
  }

  // For other taxable code changes, keep total constant
  const subtotal = currentTotal / (1 + newRate)
  const taxAmt = currentTotal - subtotal

  return {
    subtotal: Math.round(subtotal * 100) / 100,
    taxAmt: Math.round(taxAmt * 100) / 100,
    total: currentTotal,
  }
}

/**
 * Calculate amounts when changing from one tax code to another
 * This handles all combinations: I↔E, I↔Z, E↔Z, etc.
 */
export function handleTaxCodeChange(
  currentSubtotal: number,
  currentTaxAmt: number,
  currentTotal: number,
  newTaxCode: string,
  oldTaxCode?: string,
): TaxCalculationResult {
  return recalculateAmountsOnTaxCodeChange(
    currentSubtotal,
    currentTaxAmt,
    currentTotal,
    newTaxCode,
    oldTaxCode,
  )
}
